import React from "react";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

/** For two child components/nodes, horizontally split.
 * If you need the layout to swap to Vertical in Mobile, use HorizontalResponsiveLayout instead.
 * 
 This is how the ratio array works:
 - "50%-50%"= [1, 1], ==> equal split in 2 parts - each parts occupy 1 column in a 2 column layout
 - "10%/80%/10%"= [1, 8, 1], ==> Middle child expands to the max number of remaining columns (8) and outer children are fixed to 1 column each side, total columns = 10 as default
 - "33%-66%"= [1, 2], ==> split with 1st part spreading on 1/3 and second part spreading across 2/3, first part occupies 1 column and second part 2 columns in a 3 column layout
 - "25%-75%"= [1, 3], ==> split with 1st part spreading on 1/4 and second part spreading across 3/4, first part occupies 1 column and second part 3 columns in a 4 column layout
 - "1/1/1"= [1, 1, 1], ==> Equal third of the space for each part, each part occupies 1 column in a 3 column layout
 - "w-4 / flex-1 / w-3"= [4, 'flex-1',3], ==> 3 parts with middle part expanding to occupy the space, uses flex and numbers each side represent the x in w-x tailwind classes
*/
const HorizontalLayout = ({
  children,
  ratio = [1, 2],
  px = 4,
  py = 4,
  className = "",
  gap = 4,
}) => {
  const ratioValues = ratio || [1, 1];
  const flexMiddle = ratioValues.includes("flex-1");
  const totalColumns = ratioValues.reduce(
    (acc, val) => acc + (typeof val === "string" ? 1 : val),
    0,
  );

  return !!flexMiddle ? (
    <div
      data-id={"horizontal-layout-wrapper"}
      className={`flex flex-row gap-${gap} justify-center items-center px-${px} py-${py} ${className}`}
    >
      {React.Children.toArray(children).map(
        (child, index) =>
          ratioValues[index] !== 0 && (
            <div
              key={index}
              className={`h-full ${index === 1 ? "flex-1" : `w-${ratioValues[index]}`}`}
            >
              {child}
            </div>
          ),
      )}
    </div>
  ) : (
    <div
      className={`grid grid-cols-${totalColumns} gap-${gap}  items-center px-${px} py-${py} ${className}`}
    >
      {React.Children.toArray(children).map((child, index) => (
        <div
          data-id={"horizontal-layout-child"}
          key={index}
          className={` h-full col-span-${ratioValues[index]} content-center`}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export const horizontalLayoutControls = {
  children: {
    propType: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.element,
      PropTypes.string,
    ]),
    description: "Card content",
    disable: true,
  },
  ratio: {
    propTypes: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ),
    controlType: "select",
    options: [
      [1, 1],
      [1, 2],
      [1, 3],
      [1, 8, 1],
    ],
    description: "Spread ratio for the first half of the content",
  },
  gap: {
    propTypes: PropTypes.number,
    controlType: "select",
    options: [0, 1, 2, 3, 4, 5, 6],
    description: "Gap between the 2 sections on the card",
  },
  px: {
    propTypes: PropTypes.number,
    controlType: "select",
    options: [0, 1, 2, 3, 4],
    description: "Padding on the left and right of the card",
  },
  py: {
    propTypes: PropTypes.number,
    controlType: "select",
    options: [0, 1, 2, 3, 4],
    description: "Padding on the top and bottom of the card",
  },
  className: COMMON_CONTROLS.className,
};

HorizontalLayout.propTypes = AppUtils.getPropTypeFromControls(
  horizontalLayoutControls,
);

export default HorizontalLayout;
